import type { RenewalSummaryJobStatus, RenewalSummaryStage } from '@domain/schemas/renewal-summary.schema';

/**
 * One model call does the real work, so there is no true percentage. The
 * worker reports a stage; the bar fills from the previous stage's ceiling
 * toward the current one as time passes, and only reads 100% on success.
 */
const STAGE_RANGE: Record<RenewalSummaryStage, { floor: number; ceiling: number }> = {
  COMPARING: { floor: 0, ceiling: 15 },
  REVIEWING: { floor: 15, ceiling: 90 },
  DRAFTING: { floor: 90, ceiling: 98 },
};

/** How long the bar takes to get (almost all of) the way to the stage ceiling. */
export const STAGE_EASE_MS = 90_000;

interface EstimateProgressArgs {
  status: RenewalSummaryJobStatus;
  stage?: RenewalSummaryStage;
  /** ISO time the job started (or was created, before the worker picked it up). */
  startedAt?: string;
  now: number;
}

/**
 * Whole-number percentage, 0–100. Each stage starts at the previous stage's
 * ceiling; `useEstimatedProgress` holds the highest value seen per job.
 */
export const estimateProgress = ({ status, stage, startedAt, now }: EstimateProgressArgs): number => {
  if (status === 'SUCCEEDED') return 100;
  const { floor, ceiling } = STAGE_RANGE[stage ?? 'COMPARING'];
  const started = startedAt ? Date.parse(startedAt) : NaN;
  const elapsed = Number.isNaN(started) ? 0 : Math.max(0, now - started);
  // Ease out: quick at first, slowing as it nears the ceiling, ~95% of the way at STAGE_EASE_MS.
  const fraction = 1 - Math.exp((-3 * elapsed) / STAGE_EASE_MS);
  return Math.floor(floor + (ceiling - floor) * fraction);
};
